/**
 * The phone console's own reads (src/aithora/v3/mobile-console.ts). Everything else on the phone
 * reads the same routes the web console reads.
 */
import { route } from './route';
import { AITHORA_PREFIX } from './prefix';

const A = AITHORA_PREFIX;

export type MobileTone = 'red' | 'amber' | 'blue' | 'grey';

/** One "Since 6 AM" tile (Super Admin phone Today, design saTiles). */
export interface MobileSinceSixTile {
  key: 'pulse' | 'atlas' | 'pipeline' | 'intel';
  label: string;
  value: string;
  sub: string;
  /** Something new since 6 AM: counts toward "N updates". */
  changed: boolean;
  /** The tab the tile opens. */
  go: 'alerts' | 'approvals' | 'atlas';
  basis: 'measured' | 'modeled';
}

/** GET /mobile/team/today?tz= (Super Admin, Operator). */
export interface MobileTeamToday {
  real: boolean;
  /** Super Admin only (null for an Operator): what changed since 6 AM in the viewer's zone. */
  sinceSix?: { since: string; tz: string; updates: number; tiles: MobileSinceSixTile[] } | null;
  pulse: {
    state: 'ok' | 'degraded' | 'down' | 'unknown';
    /** Plain names of the services down and degraded at the last beat. */
    down: string[];
    degraded: string[];
    services: number;
    checkedAt: string | null;
  };
  /** Your to do: emergencies nobody has claimed (oldest first) and incidents nobody owns. */
  todo: {
    emergencies: { id: string; residentName: string | null; facilityName: string | null; createdAt: string }[];
    /** Open (not yet acknowledged) Pulse incidents; take one with POST /api/pulse/incidents/:id/ack. */
    incidents: { id: string; ref: string | null; title: string; scope: string; facilityName: string | null; openedAt: string; note: string | null }[];
  };
  kpis: {
    live: number;
    onboarding: number;
    /** Residents in live buildings. */
    residents: number;
    /** Residents in every building served, live or not: the buildings the emergencies are counted across. */
    residentsAll?: number;
    emergencies: number;
    unclaimed: number;
    /** "October", in the buildings' time zone. */
    month: string;
    /** Claims paid through Aithora this month at every live building (not subscription revenue). */
    collected: { usd: number | null; basis: 'measured' | null; reason: string | null };
  };
}

export interface MobileFacilityRow {
  id: string;
  name: string;
  /** "City, ST" from the catalog record, or null. */
  where: string | null;
  residents: number | null;
  live: boolean;
  /** Live (pilot started), onboarding (signed, not live) or not live yet (neither). */
  stage?: 'live' | 'onboarding' | 'not_live';
  /** What is wrong right now ("Emergency", an open incident's title), "Onboarding", "Not live yet" or "Clear". */
  status: string;
  tone: MobileTone;
  real: boolean;
}
export interface MobileCatalogRow { id: string; name: string; city: string | null; state: string | null; type: string; beds: number | null }

/** GET /mobile/facilities?q=&limit= (Super Admin, Operator). */
export interface MobileFacilities {
  real: boolean;
  facilities: MobileFacilityRow[];
  live: number;
  onboarding: number;
  /** Active buildings neither live nor signed. */
  notLive?: number;
  /** Every active building (live, onboarding and not live yet). */
  total?: number;
  /** Catalog matches for a search of 2 characters or more; empty otherwise. */
  catalog: MobileCatalogRow[];
  catalogTotal: number;
}

/** One minute of a service: its status and the bar's height in percent (null: no beat). */
export type MobileStripBar = { s: string; h: number } | null;
export interface MobileStripService {
  signal: string;
  label: string;
  status: string;
  metric: string | null;
  bars: MobileStripBar[];
}
/** GET /mobile/pulse/strips (Super Admin, Operator, Dev). */
export interface MobilePulseStrips {
  real: boolean;
  windowMinutes: number;
  checkedAt: string | null;
  services: MobileStripService[];
  reason: string | null;
  /**
   * PROPOSED (not served yet): the buildings on the live runtime and their residents, the
   * console System Pulse's "3 connected buildings · 212 residents" (V9 wording). The other
   * pilot buildings run their pilot but are not on the runtime. Absent: the phone draws no line.
   */
  connected?: { buildings: number; residents: number | null } | null;
}

export interface MobileDevIncident {
  id: string;
  ref: string | null;
  title: string;
  scope: string;
  signal: string;
  severity: string;
  status: 'open' | 'acknowledged' | 'resolved' | string;
  openedAt: string;
  acknowledgedAt: string | null;
  owner: string | null;
  resolvedAt: string | null;
  autoResolved: boolean;
  note: string | null;
  /** The open outage page sent to me that I may acknowledge (POST /pulse-v3/pages/:id/ack), or null. */
  myPageId: string | null;
}
/** GET /mobile/dev/incidents (Super Admin, Operator, Dev). */
export interface MobileDevIncidents {
  real: boolean;
  incidents: MobileDevIncident[];
}

/** GET /mobile/founding50 (Sales, Super Admin, Operator). */
export type MobileFounding50 =
  | { real: boolean; configured: false; code: string; reason: string }
  | { real: boolean; configured: true; code: string; pilotDays: number; maxUses: number | null; used: number; open: number | null; active: boolean };

export interface MobileContact {
  id: string;
  name: string;
  title: string | null;
  facilityId: string | null;
  facilityName: string | null;
  /** The stage of the building's latest deal ("Pilot signed"), or null with no deal. */
  stage: string | null;
  won: boolean;
  lost: boolean;
}
/** GET /mobile/contacts?q=&limit= (Sales, Super Admin, Operator). */
export interface MobileContacts {
  real: boolean;
  contacts: MobileContact[];
  /** Catalog matches for a search of 2 characters or more; empty otherwise. */
  catalog: MobileCatalogRow[];
  contactsTotal: number;
  catalogTotal: number;
}

/** Super Admin / Operator Today: the Pulse line, the figures and the to do (facilities: the phone's facility filter narrows the to do; tz: the phone's zone for Super Admin's Since 6 AM tiles). */
export const mobileTeamToday = route<MobileTeamToday, { facilities?: string; tz?: string }>()('GET', `${A}/mobile/team/today`, { honest: true });
export const mobileFacilities = route<MobileFacilities, { q?: string; limit?: number }>()('GET', `${A}/mobile/facilities`, { honest: true });
export const mobilePulseStrips = route<MobilePulseStrips>()('GET', `${A}/mobile/pulse/strips`, { honest: true });
export const mobileDevIncidents = route<MobileDevIncidents>()('GET', `${A}/mobile/dev/incidents`, { honest: true });
export const mobileFounding50 = route<MobileFounding50>()('GET', `${A}/mobile/founding50`, { honest: true });
export const mobileContacts = route<MobileContacts, { q?: string; limit?: number }>()('GET', `${A}/mobile/contacts`, { honest: true });
